import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '../../icon/Icon';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { firstPageSlice } from '../../../../store/firstPageSlice';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { checkIsEnteredFromIonAdPage } from '../../../../utils/checker/checkIsEnteredFromIonAdPage.util';
import { BackButtonProps } from '../MainHead';

export const BackButton = ({ backButton }: { backButton: BackButtonProps }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useAppDispatch();
  const { isFirstPageHasBackButton } = useAppSelector((state) => state.firstPage);
  const { setIsFirstPageHasBackButton } = firstPageSlice.actions;
  const [isFromLogin, setIsFromLogin] = useState<boolean>(false);

  useEffect(() => {
    if (backButton.isShow && isFirstPageHasBackButton === false && location.key === 'default') {
      dispatch(setIsFirstPageHasBackButton(true));
    }
  }, [backButton.isShow, isFirstPageHasBackButton, location.key]);

  useEffect(() => {
    if (location.state?.loginFinished) {
      setIsFromLogin(true);
    }
  }, [location.state]);

  // 앱 내부로 한 번이라도 이동했다면 히스토리가 쌓여 있으므로 기존 뒤로가기 로직을 그대로 따른다
  // (location.key === 'default'는 이 세션의 최초 진입 페이지에 머물러 있다는 의미)
  // 새 탭으로 열려 pop 할 히스토리가 없는 경우에는 뒤로가기가 먹통이 되므로 기존 로직으로 넘긴다
  const isEnteredFromIonAdPage =
    location.key === 'default' && window.history.length > 1 && checkIsEnteredFromIonAdPage({ search: location.search });

  const handleBackButtonClick = () => {
    // 아이온 광고 페이지에서 넘어와 아직 진입 페이지에 머물러 있는 경우
    // 앱 안쪽으로 보내지 않고 히스토리를 pop 해서 광고 페이지로 되돌려보낸다
    if (isEnteredFromIonAdPage) {
      navigate(-1);
      return;
    }

    if (backButton.isShow && backButton.directlyPassedURL && isFromLogin) {
      navigate(backButton.directlyPassedURL);
      return;
    }

    // 최초 진입 페이지가 back button이 없었거나 (정상적인 상황, 브라우저 히스토리를 그대로 이용해도 됨), 로그인 직후가 아닌 경우
    if (!isFirstPageHasBackButton) {
      navigate(-1);
      return;
    }

    // 최초 진입 페이지가 back button이 있었다면 (예외적인 상황, 브라우저 히스토리를 그대로 이용하면 외부 페이지로 갈 수도 있으므로 이용하면 안됨)
    // 페이지를 이동하기 전에, 브라우저 히스토리를 이용할 수 있는 정상적인 상황으로 돌아가므로 전역 상태 변경
    dispatch(setIsFirstPageHasBackButton(false));

    // 직접 지정한 페이지가 있다면 해당 페이지로 이동
    if (backButton.isShow && backButton.directlyPassedURL) {
      navigate(backButton.directlyPassedURL);
      return;
    }

    // 직접 지정한 페이지가 없다면 홈 페이지로 이동
    navigate(pageInfo.homePage.path);
  };

  return (
    <BackButtonContainer>
      <Button
        type={'button'}
        onClick={() => {
          handleBackButtonClick();
          backButton.onClick?.();
        }}
      >
        <Icon name={'ArrowLeft'} size={'XLarge'} />
      </Button>
    </BackButtonContainer>
  );
};

const BackButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;

  padding-right: 16px;
`;

const Button = styled.button``;
